/** Reusable approval bar for stages. Supports feedback-aware regenerate.
 * When user clicks "Apply feedback & regenerate" we:
 *   1. POST /api/approvals with action="regenerate" + comment (persists feedback)
 *   2. POST /api/agents/run with extra_input.feedback = comment to re-run the agent
 */
import { Check, X, RefreshCcw, MessageSquare, History } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { stageStatusColor } from "@/lib/constants";

// stage key → agent id
const STAGE_AGENT_MAP = {
    competitor_analysis: "competitor_analyst",
    brand_dna: "brand_analyst",
    business_plan: "business_plan",
    research: "research",
    concepts: "creative_director",
    script: "script",
    storyboard: "storyboard",
    prompts: "prompt_engineer",
    presentation: "presentation",
    post_production: "post_production",
};

export default function ApprovalBar({ project, stage, onUpdated }) {
    const s = (project.stages || {})[stage] || {};
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const versions = s.versions || [];

    // Phase 2C Wiring Hotfix — surface a warning when the auto-pipeline
    // is waiting on a DIFFERENT stage than this one. Approving here is
    // still allowed (panel-level approval should always work), but the
    // CEO ticker won't move until the awaited stage is approved.
    const ap = project.auto_pipeline || {};
    const awaiting = ap.awaiting_stage || "";
    const mismatch = awaiting && awaiting !== stage;

    const _renderStructured409 = (detail) => {
        // Sprint UI — structured 409 payloads emitted by
        // workflow.transition_stage. Render human message + hint.
        if (!detail || typeof detail !== "object") return null;
        const err = detail.error_type;
        const msg = detail.message || "";
        if (!err) return null;
        if (err === "campaign_inputs_not_ready") {
            const miss = (detail.missing_campaign_inputs || []).join(", ");
            return `${msg}${miss ? ` (missing: ${miss})` : ""}`;
        }
        if (err === "prereq_not_satisfied") {
            return `${msg}`;
        }
        if (err === "no_output_to_approve") {
            return `${msg} Run the agent first.`;
        }
        if (err === "skip_reason_required") {
            return `${msg}`;
        }
        return msg || String(err);
    };

    const act = async (action) => {
        setBusy(true);
        try {
            const { data } = await api.post("/approvals", { project_id: project.id, stage, action, comment });
            toast.success(`Stage ${action.replaceAll("_", " ")}.`);
            if (action !== "regenerate") setComment("");
            onUpdated && onUpdated(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const structured = _renderStructured409(detail);
            toast.error(structured || e?.userMessage || (typeof detail === "string" ? detail : "") || "Failed");
        } finally { setBusy(false); }
    };

    const applyAndRegenerate = async () => {
        const agent = STAGE_AGENT_MAP[stage];
        if (!agent) {
            toast.error("This stage has no agent to regenerate.");
            return;
        }
        setBusy(true);
        try {
            // persist feedback on the stage first
            if (comment.trim()) {
                await api.post("/approvals", { project_id: project.id, stage, action: "regenerate", comment });
            }
            // trigger the agent with feedback
            await api.post("/agents/run", {
                project_id: project.id,
                agent,
                extra_input: { feedback: comment },
            });
            toast.success("Regenerated with feedback.");
            setComment("");
            onUpdated && onUpdated();
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Regenerate failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-zinc-900 bg-[#0c0c0c] rounded-sm p-4 mt-4" data-testid={`approval-${stage}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <span className="label-micro">Approval</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${stageStatusColor(s.status)}`}>
                        {(s.status || "draft").replaceAll("_"," ").toUpperCase()}
                    </span>
                    {s.last_error && (
                        <span className="text-[10px] font-mono text-red-400" title={s.last_error}>
                            · last error
                        </span>
                    )}
                </div>
                <button
                    data-testid={`history-${stage}`}
                    onClick={() => setShowHistory(v => !v)}
                    className="text-[10px] font-mono text-zinc-500 hover:text-white inline-flex items-center gap-1"
                >
                    <History className="w-3 h-3" /> {versions.length} prior version{versions.length === 1 ? "" : "s"}
                </button>
            </div>

            <div className="flex gap-2 flex-wrap mb-3">
                <button data-testid={`approve-${stage}`} onClick={() => act("approve")} disabled={busy} className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button data-testid={`reject-${stage}`} onClick={() => act("reject")} disabled={busy} className="btn-danger text-xs px-3 py-1.5 disabled:opacity-50">
                    <X className="w-3.5 h-3.5" /> Reject
                </button>
                <button data-testid={`changes-${stage}`} onClick={() => act("request_changes")} disabled={busy} className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50">
                    <MessageSquare className="w-3.5 h-3.5" /> Request changes
                </button>
                <button data-testid={`regenerate-${stage}`} onClick={applyAndRegenerate} disabled={busy} className="btn-accent text-xs px-3 py-1.5 disabled:opacity-50">
                    <RefreshCcw className="w-3.5 h-3.5" /> {comment.trim() ? "Apply feedback & regenerate" : "Regenerate"}
                </button>
            </div>
            {mismatch && (
                <div
                    className="text-[11px] text-amber-300 bg-amber-500/5 border border-amber-500/30 rounded-sm px-2 py-1.5 mb-3"
                    data-testid={`awaiting-mismatch-${stage}`}
                    title={`Auto-pipeline is awaiting ${awaiting}, not ${stage}.`}
                >
                    ⚠ The CEO ticker is waiting on{" "}
                    <span className="text-white font-mono">
                        {awaiting.replace(/_/g, " ")}
                    </span>
                    , not this stage. Approving here won't resume the auto
                    pipeline until you approve{" "}
                    <span className="text-white font-mono">{awaiting}</span> too.
                </div>
            )}
            <textarea
                data-testid={`comment-${stage}`}
                rows={2} value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Feedback / notes — will be passed to the agent on regenerate"
                className="w-full bg-black border border-zinc-800 text-sm rounded-sm px-3 py-2 focus:border-zinc-500 outline-none"
            />

            {showHistory && versions.length > 0 && (
                <div className="mt-3 space-y-2">
                    <div className="label-micro">Version history</div>
                    {versions.map((v, i) => (
                        <details key={i} className="border border-zinc-900 rounded-sm">
                            <summary className="px-3 py-2 text-xs font-mono cursor-pointer text-zinc-400 hover:text-white">
                                Version {versions.length - i} · {v.saved_at?.slice(0, 19).replace("T", " ")}
                            </summary>
                            <pre className="text-[10px] font-mono text-zinc-400 p-3 overflow-x-auto max-h-60">
                                {JSON.stringify(v.output, null, 2)}
                            </pre>
                        </details>
                    ))}
                </div>
            )}

            {s.comments?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                    <div className="label-micro">Team comments</div>
                    {s.comments.map((c, i) => (
                        <div key={i} className="text-xs text-zinc-400 border-l-2 border-zinc-700 pl-3">
                            <span className="text-zinc-500 font-mono">{c.by}</span>: {c.text}
                        </div>
                    ))}
                </div>
            )}
            {s.client_actions?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                    <div className="label-micro">Client actions</div>
                    {s.client_actions.map((c, i) => (
                        <div key={i} className="text-xs border-l-2 border-emerald-500/40 pl-3">
                            <span className="text-emerald-400 font-mono uppercase">{c.action}</span>{" "}
                            <span className="text-zinc-300">{c.name}</span>: <span className="text-zinc-400">{c.comment || "—"}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
